import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Empty, Spin, Skeleton, message, Button, Drawer, Tag, Space, Descriptions } from 'antd';
import { CheckCircleFilled, ExclamationCircleFilled, ReloadOutlined } from '@ant-design/icons';
import { Card } from 'antd';
import Helpers from 'helpers/Helpers';
import ReportStatisticsApi from 'apis/api/ReportStatistics';
import ModalComponent from '@components/common/Modal';
import Table, { IColumnTable } from '@components/table-devextreme/Table';
import TableTitle from '@components/table-devextreme/TableTitle';
import moment from 'moment';
import { MINPAGESIZE3 } from 'global/Global';
import { useTranslation } from 'react-i18next';

// ----------------------------------------------------------------------------------
// Design tokens
// ----------------------------------------------------------------------------------
const TEXT = {
  eyebrow: 'text-[12px] text-gray-500',
  caption: 'text-[12px] text-gray-400',
  title: 'text-[16px] font-semibold text-gray-900',
  kpiValue: 'text-[18px] font-semibold tabular-nums'
};

// exceljs cần ARGB 8 ký tự (FF + RRGGBB)
const EXCEL_ROW_STYLE_BY_INDEX: Record<number, { bg: string; color: string; bold?: boolean }> = {
  0: { bg: 'FFFFF8CC', color: 'FF000000', bold: true }, // vàng - NỢ ĐẦU KỲ / CÔNG NỢ HIỆN TẠI
  1: { bg: 'FFFEF2F2', color: 'FFB91C1C', bold: true }, // đỏ - Chiết khấu / Tiền mặt đã nhận
  2: { bg: 'FFFFFF', color: '000000', bold: true }, // teal - VAT hóa đơn
  3: { bg: 'FFF0FDFA', color: 'FF0F766E', bold: true },
  4: { bg: 'FFF3E8FF', color: 'FF7E22CE', bold: true }, // tím   - Trả hàng,
  5: { bg: 'FFF3E8FF', color: 'FF7E22CE', bold: true } // tím   - Trả hàng
};

// Cùng bảng màu dòng đặc biệt với ReportDoanhThuV3 để 2 bảng nhìn giống hệt nhau
const ROW_STYLE_BY_INDEX: Record<number, { bg: string; color: string; borderColor: string; fontWeight?: string }> = {
  1: { bg: '#fef2f2', color: '#b91c1c', borderColor: '#ef4444', fontWeight: '600' },
  3: { bg: '#f0fdfa', color: '#0f766e', borderColor: '#14b8a6', fontWeight: '600' },
  4: { bg: '#f3e8ff', color: '#7e22ce', borderColor: '#a855f7', fontWeight: '600' },
  5: { bg: '#f3e8ff', color: '#7e22ce', borderColor: '#a855f7', fontWeight: '600' }
};

const MONEY_DATHU_FIELD_MAP: Record<string, string> = {
  thanhTien: 'sanPhamDaThu',
  thuTienPhi: 'phiDaThu',
  thuVAT: 'vatDaThu',
  thuChenhLech: 'chenhLechDaThu',
  thuGoiCuoc: 'goiCuocDaThu',
  tienCong: 'congDaThu'
};

const ZERO_HIDDEN_FIELDS = [
  'soLuong',
  'donGia',
  'thanhTien',
  'thuTienPhi',
  'thuVAT',
  'thuChenhLech',
  'thuGoiCuoc',
  'tienCong',
  'thuTien'
] as const;

const nullifyZero = (value: any): any => {
  if (value === null || value === undefined || value === '') return null;
  const num = Number(value);
  if (Number.isNaN(num)) return value;
  return num === 0 ? null : num;
};

// ----------------------------------------------------------------------------------
// Kiểu dữ liệu
// ----------------------------------------------------------------------------------
interface IDonHangDebt {
  saleAccount: string[];
  tenKhachHang: string;
  maKhachHang: string;
  ngayThuTien: string;
  soHoaDon: string;
  dienGiai: string;
  soLuong: number;
  donGia: number;
  thanhTien: number;
  thuTien: number;
  thuChenhLech: number;
  thuTienPhi: number;
  no: number;
  maDonHang: string;
  serial: string;
  bienSoXe: string;
  dienGiaiChung: string;
  index: number;
  daThanhToan: boolean;
  staffCreate: string;
  cashStatus: string;
  thuVAT: number;
  thuGoiCuoc: number;
  tienCong: number;
  tongTien: number;
  snapShotData: boolean;
  snapShotFlag: boolean;
  snapShotId: number;
  sanPhamDaThu: number;
  phiDaThu: number;
  vatDaThu: number;
  chenhLechDaThu: number;
  goiCuocDaThu: number;
  congDaThu: number;
}

interface IDetailKhachHangResponse {
  tenKhachHang: string;
  maKhachHang: string;
  soDienThoai: string;
  tonDauKy: number;
  donHangs: IDonHangDebt[];
}

interface IDetailTheoKhachHangBody {
  pageSize: number;
  pageIndex: number;
  orderTypeId: number[];
  from: string;
  to: string;
  maDonHang: string;
  orderStatus: string;
  companyBranchId: number;
  maKhachHang: string;
  typeDate: number;
  staffCreate: string;
  loaiHinhHopTac: number;
  companyName: string;
  staffExecute: string;
  hinhThucThanhToan: string;
  typeBranch: number;
  saleAccount: string;
}

interface ICustomerDebtDetailModalProps {
  visible: boolean;
  onClose: () => void;
  maKhachHang: string;
  saleAccount: string;
  from: string;
  to: string;
}

const formatMoney = (val?: number) => Helpers.FormatMoney(val || 0, true);

// ----------------------------------------------------------------------------------
// Phân loại dòng (chỉ dùng để tính KPI bridge, việc tô màu bảng dùng thẳng `index`
// giống hệt ReportDoanhThuV3 vì 2 API trả về cùng cấu trúc "index đặc biệt")
// ----------------------------------------------------------------------------------
const isFrameRow = (row: IDonHangDebt) => {
  const dg = (row.dienGiai || '').trim();
  return dg === 'TỔNG HỢP' || row.snapShotFlag || dg.startsWith('CHỐT CÔNG NỢ') || row.snapShotData;
};

const CashStatusTag = ({ row }: { row: IDonHangDebt }) => {
  if (row.cashStatus === 'Đã nhận tiền') return <Tag color="success">Đã thu</Tag>;
  if (row.cashStatus === 'Chưa nhận tiền') return <Tag color="error">Chưa thu</Tag>;
  return <Tag>—</Tag>;
};

const TransactionDetailDrawer = ({
  row,
  open,
  onClose
}: {
  row: IDonHangDebt | null;
  open: boolean;
  onClose: () => void;
}) => {
  if (!row) return null;
  const d = row.ngayThuTien ? new Date(row.ngayThuTien) : null;

  return (
    <Drawer title="Chi tiết giao dịch" open={open} onClose={onClose} width={480} destroyOnClose>
      <Space direction="vertical" size="middle" className="w-full">
        <Descriptions column={1} size="small" bordered>
          <Descriptions.Item label="Diễn giải">{row.dienGiai || '—'}</Descriptions.Item>
          <Descriptions.Item label="Thời gian">{d ? d.toLocaleString('vi-VN') : '—'}</Descriptions.Item>
          <Descriptions.Item label="Số hóa đơn">{row.soHoaDon || '—'}</Descriptions.Item>
          <Descriptions.Item label="Mã đơn hàng">{row.maDonHang || '—'}</Descriptions.Item>
          <Descriptions.Item label="Serial">{row.serial || '—'}</Descriptions.Item>
          <Descriptions.Item label="Biển số xe">{row.bienSoXe || '—'}</Descriptions.Item>
          <Descriptions.Item label="Số lượng">{row.soLuong ?? '—'}</Descriptions.Item>
          <Descriptions.Item label="Đơn giá">{formatMoney(row.donGia)}</Descriptions.Item>
          <Descriptions.Item label="Thành tiền">{formatMoney(row.thanhTien)}</Descriptions.Item>
          <Descriptions.Item label="Đã thu">{formatMoney(row.thuTien)}</Descriptions.Item>
          <Descriptions.Item label="Thu VAT">{formatMoney(row.thuVAT)}</Descriptions.Item>
          <Descriptions.Item label="Thu phí">{formatMoney(row.thuTienPhi)}</Descriptions.Item>
          <Descriptions.Item label="Thu chênh lệch">{formatMoney(row.thuChenhLech)}</Descriptions.Item>
          <Descriptions.Item label="Thu gói cước">{formatMoney(row.thuGoiCuoc)}</Descriptions.Item>
          <Descriptions.Item label="Thu công">{formatMoney(row.tienCong)}</Descriptions.Item>
          <Descriptions.Item label="Dư nợ">
            <span className="font-semibold">
              {row.no > 0 ? '+' : ''}
              {formatMoney(row.no)}
            </span>
          </Descriptions.Item>
          <Descriptions.Item label="Nhân viên tạo">{row.staffCreate || '—'}</Descriptions.Item>
        </Descriptions>
      </Space>
    </Drawer>
  );
};

const TABLE_ID = 'customerdebtdetailmodal';

export default function CustomerDebtDetailModal({
  visible,
  onClose,
  maKhachHang,
  saleAccount,
  from,
  to
}: ICustomerDebtDetailModalProps) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<IDetailKhachHangResponse | null>(null);
  const [customerInfo, setCustomerInfo] = useState<{ tenKhachHang: string; maKhachHang: string; soDienThoai: string }>({
    tenKhachHang: '',
    maKhachHang: '',
    soDienThoai: ''
  });
  const { t } = useTranslation();

  // Drawer
  const [drawerRow, setDrawerRow] = useState<IDonHangDebt | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const fetchDetail = async () => {
    if (!maKhachHang || !saleAccount) return;
    setLoading(true);
    try {
      const body: IDetailTheoKhachHangBody = {
        pageSize: 0,
        pageIndex: 0,
        orderTypeId: [0],
        from,
        to,
        maDonHang: '',
        orderStatus: '',
        companyBranchId: 0,
        maKhachHang,
        typeDate: 1,
        staffCreate: '',
        loaiHinhHopTac: 0,
        companyName: '',
        staffExecute: '',
        hinhThucThanhToan: '',
        typeBranch: 0,
        saleAccount
      };

      const res = await ReportStatisticsApi.DetailTheoKhachHang(body);

      if (res?.data?.error) {
        message.error(res?.data?.message || 'Có lỗi khi tải chi tiết công nợ');
        setData(null);
      } else {
        setData(res?.data?.data ?? null);

        setCustomerInfo({
          tenKhachHang: res.data.data.tenKhachHang || '',
          maKhachHang: res.data.data.maKhachHang || '',
          soDienThoai: res.data.data.soDienThoai || ''
        });
      }
    } catch (error) {
      message.error('Có lỗi khi tải chi tiết công nợ');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visible) {
      fetchDetail();
    }
  }, [visible, maKhachHang, saleAccount, from, to]);

  const list = data?.donHangs || [];

  const snapshotRow = useMemo(() => {
    return [...list].reverse().find(r => r.snapShotFlag || r.dienGiai === 'TỔNG HỢP') || list[list.length - 1];
  }, [list]);

  const currentDebt = snapshotRow?.no ?? 0;

  const openDrawer = (row: IDonHangDebt) => {
    setDrawerRow(row);
    setDrawerOpen(true);
  };

  // const exportFileName = useMemo(() => {
  //   const today = moment().format('YYYYMMDD'); // Ví dụ: 20231026. Có thể đổi thành 'DDMMYYYY' nếu muốn
  //   const maKH = maKhachHang ? `${maKhachHang}` : 'ALL';

  //   return `${today}_${maKH}_ChiTietCongNo`;
  // }, [maKhachHang]);

  const exportFileName = useMemo(() => {
    const today = moment().format('DDMMYYYY');
    const maKH = maKhachHang ? `${maKhachHang}` : 'ALL';
    const tenKH = customerInfo.tenKhachHang
      ? customerInfo.tenKhachHang.replace(/\s+/g, '_') // bỏ khoảng trắng để tránh lỗi tên file trên 1 số OS
      : '';

    return [today, maKH, tenKH, 'BaoCaoDoanhThuKH'].filter(Boolean).join('_');
  }, [maKhachHang, customerInfo.tenKhachHang]);

  // -------------------------------------------------------------------------------
  // Map dữ liệu -> row cho DevExtreme table, giống hệt handleMapData của ReportDoanhThuV3
  // -------------------------------------------------------------------------------
  const dataTable = useMemo(() => {
    return list
      .map(item => {
        if (item?.index === 0) {
          return {
            ...item,
            ngayThuTienText: '',
            maDonHang: '',
            soHoaDon: '',
            soLuong: null,
            tongTien: null
          };
        }

        const zeroHidden = ZERO_HIDDEN_FIELDS.reduce((acc: Record<string, any>, field) => {
          acc[field] = nullifyZero((item as any)?.[field]);
          return acc;
        }, {});

        return {
          ...item,
          ...zeroHidden,
          ngayThuTienText: item?.ngayThuTien ? moment(item.ngayThuTien).format('DD/MM/YYYY HH:mm:ss') : '-'
        };
      })
      .map((item, idx) => ({ ...item, rowIdx: idx + 1 }));
  }, [list]);

  // -------------------------------------------------------------------------------
  // Cell renders — port từ ReportDoanhThuV3
  // -------------------------------------------------------------------------------
  const formatCustomSum = (value: any) => {
    if (value === null || value === undefined || value === '') return '';
    const num = Number(value);
    if (Number.isNaN(num) || num === 0) return '';
    return Helpers.FormatMoney(num, true);
  };

  const renderMoneyCell = (cell: any) => formatCustomSum(cell?.value);

  const renderMoneyWithDaThu = (cell: any, daThuField: string) => {
    const isSummaryRow = cell?.data?.index === 0;

    if (!isSummaryRow) {
      const val = cell?.value;
      if (val === null || val === undefined || val === '') return '';
      return Helpers.FormatMoney(Number(val), true);
    }

    const mainVal = Number(cell?.value) || 0;
    const daThuVal = Number(cell?.data?.[daThuField]) || 0;
    if (mainVal === 0 && daThuVal === 0) return '';
    return `${Helpers.FormatMoney(mainVal, true)} (${Helpers.FormatMoney(daThuVal, true)})`;
  };

  const renderTongTienCell = (cell: any) => {
    if (cell?.data?.index === 0) return '';
    return formatCustomSum(cell?.value);
  };

  const renderNoCell = (cell: any) => {
    const val = cell?.value;
    const formattedVal = val === null || val === undefined || val === '' ? '' : Helpers.FormatMoney(Number(val), true);
    if (cell?.data?.index === 0) {
      return <span className="font-bold block w-full px-1">{formattedVal}</span>;
    }
    return formattedVal;
  };

  const renderDienGiaiCell = (cell: any) => {
    const text = cell?.value || '-';
    if (cell?.data?.index === 0) return <b>{text}</b>;
    return text;
  };

  const renderStatusCell = (cell: any) => <CashStatusTag row={cell?.data} />;

  const columns: IColumnTable[] = useMemo(
    () => [
      {
        dataField: 'ngayThuTienText',
        caption: 'Ngày',
        width: 170
      },
      {
        dataField: 'maDonHang',
        caption: 'Mã đơn hàng',
        width: 110
      },
      {
        dataField: 'dienGiai',
        caption: 'Diễn giải',
        width: 200,

        cellRender: renderDienGiaiCell
      },
      {
        dataField: 'soHoaDon',
        caption: 'Số hóa đơn',
        width: 120,
        alignment: 'right'
      },
      {
        dataField: 'soLuong',
        caption: 'SL',
        width: 70,
        alignment: 'right',
        format: 'fixedPoint',
        dataType: 'number',
        formatCustomSum
      },
      {
        caption: 'Doanh thu',
        childHeader: [
          {
            dataField: 'donGia',
            caption: 'Đơn giá',
            width: 110,
            alignment: 'right',
            format: 'fixedPoint',
            dataType: 'number',
            formatCustomSum,
            cellRender: renderMoneyCell
          },
          {
            dataField: 'thanhTien',
            caption: 'Tiền sản phẩm',
            width: 110,
            alignment: 'right',
            format: 'fixedPoint',
            dataType: 'number',
            formatCustomSum,
            cellRender: (cell: any) => renderMoneyWithDaThu(cell, MONEY_DATHU_FIELD_MAP.thanhTien)
          },
          {
            dataField: 'thuTienPhi',
            caption: 'Tiền phí',
            width: 110,
            alignment: 'right',
            format: 'fixedPoint',
            dataType: 'number',
            formatCustomSum,
            cellRender: (cell: any) => renderMoneyWithDaThu(cell, MONEY_DATHU_FIELD_MAP.thuTienPhi)
          },
          {
            dataField: 'thuVAT',
            caption: 'Tiền VAT',
            width: 110,
            alignment: 'right',
            format: 'fixedPoint',
            dataType: 'number',
            formatCustomSum,
            cellRender: (cell: any) => renderMoneyWithDaThu(cell, MONEY_DATHU_FIELD_MAP.thuVAT)
          },
          {
            dataField: 'thuChenhLech',
            caption: 'Tiền chênh lệch',
            width: 110,
            alignment: 'right',
            format: 'fixedPoint',
            dataType: 'number',
            formatCustomSum,
            cellRender: (cell: any) => renderMoneyWithDaThu(cell, MONEY_DATHU_FIELD_MAP.thuChenhLech)
          },
          {
            dataField: 'thuGoiCuoc',
            caption: 'Tiền gói cước',
            width: 110,
            alignment: 'right',
            format: 'fixedPoint',
            dataType: 'number',
            formatCustomSum,
            cellRender: (cell: any) => renderMoneyWithDaThu(cell, MONEY_DATHU_FIELD_MAP.thuGoiCuoc)
          },
          {
            dataField: 'tienCong',
            caption: 'Tiền công',
            width: 110,
            alignment: 'right',
            format: 'fixedPoint',
            dataType: 'number',
            formatCustomSum,
            cellRender: (cell: any) => renderMoneyWithDaThu(cell, MONEY_DATHU_FIELD_MAP.tienCong)
          },
          {
            dataField: 'tongTien',
            caption: 'Tổng',
            width: 110,
            alignment: 'right',
            format: 'fixedPoint',
            dataType: 'number',
            formatCustomSum,
            cellRender: renderTongTienCell
          },
          {
            dataField: 'thuTien',
            caption: 'Tiền đã thu',
            width: 110,
            alignment: 'right',
            format: 'fixedPoint',
            dataType: 'number',
            formatCustomSum,
            cellRender: renderMoneyCell
          },
          {
            dataField: 'no',
            caption: 'Nợ',
            width: 110,
            alignment: 'right',
            format: 'fixedPoint',
            dataType: 'number',
            formatCustomSum,
            cellRender: renderNoCell
          }
        ]
      },
      {
        dataField: 'serial',
        caption: 'Serial',
        width: 150
      },
      {
        dataField: 'bienSoXe',
        caption: 'Biển số xe',
        width: 150
      },
      // {
      //   dataField: 'cashStatus',
      //   caption: 'Trạng thái',
      //   width: 100,
      //   alignment: 'center',
      //   cellRender: renderStatusCell
      // },
      {
        caption: 'Thông tin KH',
        childHeader: [
          {
            dataField: 'tenLienHe',
            caption: t('OrganizedSales.OrderManager.AllOrder.ContactName'),
            width: 150
          },
          {
            dataField: 'sdtLienHe',
            caption: t('OrganizedSales.OrderManager.AllOrder.ContactPhone'),
            width: 120
          },
          {
            dataField: 'loaiDon',
            caption: t('OrganizedSales.OrderManager.AllOrder.OrderType'),
            minWidth: 120
          }
        ]
      },
      {
        dataField: 'staffCreate',
        caption: 'Nhân viên tạo',
        width: 130
      }
    ],
    []
  );

  const handleExportCellStyle = useCallback((gridCell: any, excelCell: any) => {
    if (gridCell.rowType !== 'data') return;

    const index = gridCell.data?.index;
    const dataField = gridCell.column?.dataField;
    const daThuField = dataField && MONEY_DATHU_FIELD_MAP[dataField as keyof typeof MONEY_DATHU_FIELD_MAP];

    // Chỉ ghi đè giá trị export dạng "Tiền (Tiền đã thu)" ở dòng index = 0
    if (daThuField && index === 0) {
      const mainVal = Number(gridCell.value) || 0;
      const daThuVal = Number(gridCell.data?.[daThuField]) || 0;

      if (mainVal === 0 && daThuVal === 0) {
        excelCell.value = null;
      } else {
        excelCell.value = `${Helpers.FormatMoney(mainVal, true)} (${Helpers.FormatMoney(daThuVal, true)})`;
        excelCell.numFmt = '@'; // ép text, tránh Excel hiểu ngoặc là số âm
        excelCell.alignment = { ...(excelCell.alignment || {}), horizontal: 'right' };
      }
    }
    // Các dòng khác (index !== 0): không override — export theo gridCell.value mặc định
    // (đã bị null hoá khi = 0 nhờ ZERO_HIDDEN_FIELDS ở handleMapData, nên tự động ẩn đúng như UI)

    const style = EXCEL_ROW_STYLE_BY_INDEX[index as number];
    if (!style) return;

    excelCell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: style.bg }
    };
    excelCell.font = {
      ...(excelCell.font || {}),
      color: { argb: style.color },
      bold: !!style.bold
    };
  }, []);

  const handleRowPreparedStyle = (e: any): void => {
    if (e?.rowType !== 'data') return;

    const { index } = e.data || {};
    const row = e.rowElement;

    row.classList.remove('row-special-opening', 'row-index-1', 'row-index-2', 'row-index-3');
    row.style.backgroundColor = '';
    row.style.color = '';
    row.style.fontWeight = '';
    row.style.position = '';
    row.style.bottom = '';
    row.style.zIndex = '';
    row.style.boxShadow = '';

    if (index === 0) {
      row.classList.add('row-special-opening');
      row.style.fontWeight = '600';
      row.style.position = 'sticky';
      row.style.bottom = '0';
      row.style.zIndex = '999';
      row.style.backgroundColor = '#fff';
      return;
    }

    const style = ROW_STYLE_BY_INDEX[index as number];
    if (style) {
      row.classList.add(`row-index-${index}`);
      row.style.backgroundColor = style.bg;
      row.style.color = style.color;
      row.style.boxShadow = `inset 3px 0 0 0 ${style.borderColor}`;
      if (style.fontWeight) row.style.fontWeight = style.fontWeight;
    }
  };

  const exportSubtitleText = useMemo(() => {
    const { tenKhachHang, maKhachHang, soDienThoai } = customerInfo;
    return [tenKhachHang, maKhachHang ? `Mã KH: ${maKhachHang}` : '', soDienThoai ? `SĐT: ${soDienThoai}` : '']
      .filter(Boolean)
      .join(' - ');
  }, [customerInfo]);

  return (
    <ModalComponent
      title="Chi tiết công nợ khách hàng"
      visible={visible}
      onCancel={onClose}
      footer={null}
      modalSize="fullscreen"
      destroyOnClose
    >
      {loading && !data ? (
        <div className="flex flex-col gap-3">
          <Skeleton active paragraph={{ rows: 1 }} />
          <Skeleton.Button active style={{ width: '100%', height: 72 }} />
          <Skeleton active paragraph={{ rows: 8 }} />
        </div>
      ) : data ? (
        <Spin spinning={loading}>
          <div className="flex flex-col gap-4 h-full min-h-0">
            {/* Header */}
            <div className="flex items-start justify-between flex-wrap gap-3 shrink-0">
              <div>
                <div className={TEXT.title}>
                  {data.tenKhachHang || '–'} - Mã KH: {data.maKhachHang || '–'} · SĐT:{' '}
                  {data.soDienThoai || 'chưa cập nhật'}
                </div>
              </div>

              <div className="flex items-center gap-2">
                {currentDebt > 0 ? (
                  <Tag icon={<ExclamationCircleFilled />} color="error" className="m-0 px-2 py-0.5">
                    Còn nợ
                  </Tag>
                ) : (
                  <Tag icon={<CheckCircleFilled />} color="success" className="m-0 px-2 py-0.5">
                    Đã tất toán
                  </Tag>
                )}
              </div>
            </div>

            {/* Bảng giao dịch — style giống ReportDoanhThuV3 */}
            <div className="flex-1 h-[calc(90vh-110px)] overflow-hidden">
              {dataTable.length > 0 ? (
                <Table
                  tableName="Chi tiết công nợ"
                  tableId={TABLE_ID}
                  keyExpr="rowIdx"
                  fileNameExport={exportFileName}
                  exportTitle="Báo cáo doanh thu theo khách hàng"
                  exportSubtitle={exportSubtitleText}
                  columns={columns}
                  data={dataTable}
                  wrapTextData={false}
                  reload={false}
                  isStriped={false}
                  enableLoadingPanel={false}
                  hiddenAddButton={true}
                  noHiddenPaging={true}
                  // minPageSize={MINPAGESIZE3}
                  downHeight={15.8}
                  fullHeight // thêm mới — bảng luôn cao full dù ít data
                  allowExcel={true}
                  isHiddenPaging={true} //
                  scrollingMode="virtual"
                  // hiddenPaging={true}
                  onRowPreparedStyle={handleRowPreparedStyle}
                  repaintChangesOnly={true}
                  customCenterToolbar={() => <TableTitle title="Chi tiết công nợ" />}
                  onClickRow={(e: any) => {
                    if (e.rowType === 'data' && e.data?.index !== 0) openDrawer(e.data);
                  }}
                  isHiddenToolbar={false}
                  //

                  onExportCellStyle={handleExportCellStyle} // <-- THÊM DÒNG NÀY
                />
              ) : (
                <Empty className="py-10" description="Chưa có giao dịch phát sinh trong kỳ này." />
              )}
            </div>
          </div>
        </Spin>
      ) : (
        !loading && (
          <Empty description="Không có dữ liệu công nợ cho khách hàng này." className="py-16">
            <Button icon={<ReloadOutlined />} onClick={fetchDetail}>
              Tải lại
            </Button>
          </Empty>
        )
      )}

      <TransactionDetailDrawer row={drawerRow} open={drawerOpen} onClose={() => setDrawerOpen(false)} />
    </ModalComponent>
  );
}
